/* compendium\compendium.js */
/* Herald's Wake: the card compendium and deck builder.
   Every card in every deck, searchable like Scryfall; the starter decklists; the rules, keywords and
   symbols from data/rules.js; and a builder for custom decks from the cards a player owns, saved
   through HWDecks (to the Sanctum account when signed in).

   Views are hash routes so the browser's back button works:
   #cards (with ?q filters kept in ui), #card/<id>, #decks, #deck/<starter key>, #build/<deck id>, #rules. */
(function () {
  "use strict";
  var E = window.HWEngine, D = window.HWDecks, CARD = E.CARD;
  var $root = document.getElementById("hwc");
  if (!$root) return;

  /* ---------- assets: local folders, or flat Shopify assets under the section's base URL ---------- */

  var ASSETS = ($root.getAttribute("data-assets") || "").replace(/[^/]*$/, "");
  var PLAY = $root.getAttribute("data-play") || "../play/";
  var DIRS = { card: "cards", atlas: "atlas", scene: "scenes", hero: "heroes", brand: "brand", ui: "ui" };
  function asset(name) { return ASSETS + name + (window.HW_VER && HW_VER[name] ? "?v=" + HW_VER[name] : ""); }
  function img(kind, id) { return ASSETS ? asset("herald-" + kind + "-" + id + ".webp") : "../play/img/" + DIRS[kind] + "/" + id + ".webp"; }
  function wide(id) { var t = CARD[id].type; return t === "Region" || t === "Event"; }
  function cardImg(id) { return img(wide(id) ? "atlas" : "card", id); }
  function brand(file) { return ASSETS ? asset("herald-brand-" + file) : "../play/img/brand/" + file; }
  // The game's own chrome, from the card template: the gold plate and the emblem.
  ["plate", "emblem"].forEach(function (k) { $root.style.setProperty("--hw-ui-" + k, "url(" + img("ui", k) + ")"); });
  // On Sanctum the compendium is part of the full-screen game: it covers the site, and Exit is in the menu.
  var EMBEDDED = !!ASSETS;
  if (EMBEDDED) { $root.classList.add("hwc-embedded"); document.documentElement.classList.add("hw-taken"); }
  function exitUrl() {
    var r = document.referrer;
    return r && r.indexOf(location.origin) === 0 && r.indexOf(location.pathname) < 0 && r.indexOf(PLAY) < 0 ? r : "/pages/inner-sanctum";
  }
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }
  // Card text as it reads on the card: bold before a colon, the Homecoming region in italics.
  function rich(t) {
    return esc(t).split("\r").map(function (line) {
      line = line.replace(/^([^:]{1,40}):/, "<b>$1:</b>").replace(/\((Fast:[^)]*)\)/, "<i>($1)</i>");
      return line.replace(/Homecoming \((\w+)\)/, "Homecoming (<i>$1</i>)");
    }).join("<br>");
  }

  /* ---------- the card list ---------- */

  var DECK_KEYS = Object.keys(HW.decks);
  var ALL = HW.cards.concat(HW.atlas);
  var TYPES = ["Herald", "Unit", "Evolving Unit", "Equipment", "Path", "Action", "Spell", "Region", "Event"];

  function heraldName(k) { var h = CARD[D.heraldOf(k)]; return h.name; }
  function title(c) { return c.type === "Herald" ? c.name + ", " + c.title : c.location ? c.name + " / " + c.location : c.name; }
  function typeLine(c) { return (c.rarity ? c.rarity + " " : "") + c.type + (c.tag ? " · " + c.tag : ""); }
  // The starter decks a card appears in, with how many copies each.
  function homes(id) {
    var out = [];
    DECK_KEYS.forEach(function (k) {
      var d = HW.decks[k], n = d.main.filter(function (x) { return x === id; }).length + (d.atlas.indexOf(id) > -1 ? 1 : 0);
      if (CARD[id].type === "Herald" && CARD[id].deck === k) n = 1;
      if (n) out.push({ key: k, n: n });
    });
    return out;
  }
  function code(c) {
    if (!c.deck) return "";
    var mine = HW.cards.filter(function (x) { return x.deck === c.deck; }), n = mine.indexOf(c) + 1;
    var pad = function (v) { return v < 10 ? "0" + v : String(v); };
    return "HW-" + c.deck.toUpperCase().substr(0, 3) + " " + pad(n) + "/" + pad(mine.length);
  }
  function searchable(c) { return (title(c) + " " + typeLine(c) + " " + (c.text || "") + " " + (c.artist || "")).toLowerCase(); }

  var ui = { q: "", deck: "", type: "", sort: "deck" };

  function filtered() {
    var words = ui.q.toLowerCase().split(/\s+/).filter(Boolean);
    var list = ALL.filter(function (c) {
      if (ui.type && c.type !== ui.type) return false;
      if (ui.deck === "common" && c.deck !== "common") return false;
      if (ui.deck && ui.deck !== "common" && !homes(c.id).some(function (h) { return h.key === ui.deck; })) return false;
      var s = searchable(c);
      return words.every(function (w) { return s.indexOf(w) > -1; });
    });
    var rank = function (c) { return c.deck === "common" ? 90 : c.deck ? DECK_KEYS.indexOf(c.deck) * 10 : 100; };
    var by = {
      deck: function (a, b) { return rank(a) - rank(b) || TYPES.indexOf(a.type) - TYPES.indexOf(b.type) || title(a).localeCompare(title(b)); },
      name: function (a, b) { return title(a).localeCompare(title(b)); },
      cost: function (a, b) { return (a.cost == null ? 99 : a.cost) - (b.cost == null ? 99 : b.cost) || title(a).localeCompare(title(b)); },
      damage: function (a, b) { return (b.damage || 0) - (a.damage || 0) || title(a).localeCompare(title(b)); }
    };
    return list.sort(by[ui.sort]);
  }

  /* ---------- page frame ---------- */

  var TABS = [["cards", "Cards"], ["decks", "Decks"], ["rules", "Rules"]];
  function frame(active, body) {
    return '<header class="hwc-head"><a href="#cards" class="hwc-logo"><img src="' + brand("logo.webp") + '" alt="Akeroth: Herald\'s Wake"></a>' +
      '<nav class="hwc-tabs">' + TABS.map(function (t) {
        return '<a href="#' + t[0] + '" class="hwc-plate' + (t[0] === active ? " hwc-on" : "") + '">' + t[1] + "</a>"; }).join("") +
      '<a href="' + PLAY + '" class="hwc-plate hwc-play">Play</a></nav>' +
      '<button class="hwc-menu-btn" id="menu-open" aria-label="Menu" aria-expanded="' + !!menuOpen + '"></button></header>' +
      (menuOpen ? '<div class="hwc-menu" id="menu-panel"><a class="hwc-plate" href="#rules">Rules &amp; Keywords</a><a class="hwc-plate" href="' + PLAY + '">Play the Game</a>' +
        (EMBEDDED ? '<a class="hwc-plate" href="' + exitUrl() + '">Exit to Sanctum</a>' : "") + "</div>" : "") +
      '<main class="hwc-main">' + body + "</main>";
  }
  var menuOpen = false;
  document.addEventListener("click", function (e) {
    var t = e.target;
    if (t.closest && t.closest("#menu-open")) { menuOpen = !menuOpen; route(true); return; }
    if (menuOpen && !(t.closest && t.closest("#menu-panel"))) { menuOpen = false; route(true); }
  });

  function renderCards() {
    var list = filtered();
    var chip = function (key, val, label) { return '<button class="hwc-chip' + (ui[key] === val ? " hwc-on" : "") + '" data-' + key + '="' + val + '">' + label + "</button>"; };
    $root.innerHTML = frame("cards",
      '<div class="hwc-search"><input id="q" type="search" placeholder="Search names, rules text, types or artists" value="' + esc(ui.q) + '" aria-label="Search cards" autocomplete="off">' +
      '<label class="hwc-sort">Sort <select id="sort">' + [["deck", "By Deck"], ["name", "By Name"], ["cost", "By Mana Cost"], ["damage", "By Damage"]].map(function (o) {
        return '<option value="' + o[0] + '"' + (ui.sort === o[0] ? " selected" : "") + ">" + o[1] + "</option>"; }).join("") + "</select></label></div>" +
      '<div class="hwc-chips">' + chip("deck", "", "Every Deck") + DECK_KEYS.map(function (k) { return chip("deck", k, esc(HW.decks[k].name)); }).join("") + chip("deck", "common", "Commons") + "</div>" +
      '<div class="hwc-chips">' + chip("type", "", "Every Type") + TYPES.map(function (t) {
        return chip("type", t, t); }).join("") + "</div>" +
      '<p class="hwc-count">' + list.length + (list.length === 1 ? " card" : " cards") + "</p>" +
      '<div class="hwc-grid">' + list.map(function (c) {
        return '<a class="hwc-tile' + (wide(c.id) ? " hwc-wide" : "") + '" href="#card/' + c.id + '"><img loading="lazy" src="' + cardImg(c.id) + '" alt="' + esc(title(c)) + '"></a>';
      }).join("") + "</div>" +
      (list.length ? "" : '<p class="hwc-empty">No cards match. Try fewer words.</p>'));
    var q = document.getElementById("q");
    q.oninput = function () { ui.q = q.value; var at = q.selectionStart; renderCards(); var n = document.getElementById("q"); n.focus(); n.setSelectionRange(at, at); };
    document.getElementById("sort").onchange = function (e) { ui.sort = e.target.value; renderCards(); };
    each("[data-deck]", function (b) { b.onclick = function () { ui.deck = b.getAttribute("data-deck"); renderCards(); }; });
    each("[data-type]", function (b) { b.onclick = function () { ui.type = b.getAttribute("data-type"); renderCards(); }; });
  }
  function iconOf(type) { for (var i = 0; i < HW.rules.types.length; i++) if (HW.rules.types[i].type === type) return HW.rules.types[i].icon; return "unit"; }

  function renderCard(id) {
    var c = CARD[id];
    if (!c) { location.hash = "#cards"; return; }
    var stats = [];
    if (c.type === "Herald") stats.push(orb("mana", c.mana, "Starting mana"), orb("life", c.life, "Life"));
    else if (c.cost != null) stats.push(orb("mana", c.cost, c.act ? "Act cost: evolve it now" : "Mana for its effect"));
    if (c.damage != null) stats.push(orb("power", c.damage, "Damage"));
    var text = c.stages ? c.stages.map(function (s, i) {
      return '<li><b>' + ["I", "II", "III", "IV"][i] + ". " + s.name + " · " + s.bonus + " Damage:</b> " + esc(s.text) +
        ' <span>(' + (s.at === 1 ? "after your next turn" : s.at ? "after " + s.at + " of your turns" : "as it enters") + ")</span></li>"; }).join("")
      : null;
    var where = homes(id);
    $root.innerHTML = frame("cards",
      '<a class="hwc-back" href="#cards">← All cards</a>' +
      '<article class="hwc-card"><img class="' + (wide(id) ? "hwc-wide" : "") + '" src="' + cardImg(id) + '" alt="' + esc(title(c)) + '">' +
      '<div class="hwc-card-info"><h1>' + esc(title(c)) + "</h1>" +
      '<p class="hwc-typeline"><span class="hwc-icon" style="background-image:url(' + img("ui", "type-" + iconOf(c.type)) + ')"></span>' + esc(typeLine(c)) + "</p>" +
      (stats.length ? '<div class="hwc-stats">' + stats.join("") + "</div>" : "") +
      '<div class="hwc-text">' + (c.actText ? "<p class=\"hwc-play-line\">" + rich(c.actText) + "</p>" : "") + (text ? "<ol class=\"hwc-stages\">" + text + "</ol>" : "<p>" + rich(c.text) + "</p>") + "</div>" +
      (c.reposition ? '<p class="hwc-note">Reposition lets it be played on an opponent\'s turn for ' + c.reposition + " mana.</p>" : "") +
      '<dl class="hwc-meta">' +
      (where.length ? "<dt>In</dt><dd>" + where.map(function (h) { return '<a href="#deck/' + h.key + '">' + esc(HW.decks[h.key].name) + "</a>" + (h.n > 1 ? " ×" + h.n : ""); }).join(", ") + "</dd>" : "") +
      (c.realm ? "<dt>Realm</dt><dd>" + esc(c.realm) + "</dd>" : "") +
      "<dt>Artist</dt><dd>" + esc(c.artist || "TBC") + "</dd>" +
      (code(c) ? "<dt>Number</dt><dd>" + code(c) + "</dd>" : "") + "</dl>" +
      keywordNotes(c) + "</div></article>");
  }
  function orb(kind, n, label) { return '<span class="hwc-orb hwc-orb-' + kind + '" title="' + label + '"><i style="background-image:url(' + img("ui", "orb-" + kind) + ')">' + n + "</i>" + label + "</span>"; }
  // The keywords this card uses, explained underneath it.
  function keywordNotes(c) {
    var text = (c.text || "") + (c.stages ? " Evolving Act" : "");
    var hits = HW.rules.keywords.filter(function (k) { return text.indexOf(k.term.replace(/ \(N\+?\)/, "")) > -1; });
    if (!hits.length) return "";
    return '<div class="hwc-notes"><h2>Rules notes</h2><dl>' + hits.map(function (k) { return "<dt>" + esc(k.term) + "</dt><dd>" + k.text + "</dd>"; }).join("") + "</dl></div>";
  }

  /* ---------- decklists ---------- */

  // A decklist grouped by card type, each line hoverable for a preview.
  function decklist(list) {
    // Regions go to the Atlas; the Events are shuffled into the deck, so they list with it.
    var regions = list.atlas.filter(function (id) { return CARD[id].type === "Region"; });
    var deck = list.main.concat(list.atlas.filter(function (id) { return CARD[id].type === "Event"; }));
    var groups = {};
    deck.forEach(function (id) { var t = CARD[id].type; (groups[t] = groups[t] || {})[id] = (groups[t][id] || 0) + 1; });
    var atlas = regions.map(function (id) { return line(id, 1); }).join("");
    return '<div class="hwc-list">' +
      '<section><h3>Herald</h3><ul>' + line(D.heraldOf(list.herald), 1) + "</ul><h3>Atlas <span>" + regions.length + "</span></h3><ul>" + atlas + "</ul><h3>Deck <span>" + deck.length + "</span></h3></section>" +
      '<section>' + TYPES.filter(function (t) { return groups[t]; }).map(function (t) {
        var ids = Object.keys(groups[t]), n = 0; ids.forEach(function (id) { n += groups[t][id]; });
        return "<h3>" + t + (t === "Equipment" ? "" : "s") + " <span>" + n + "</span></h3><ul>" + ids.map(function (id) { return line(id, groups[t][id]); }).join("") + "</ul>";
      }).join("") + "</section></div>";
  }
  function line(id, n) {
    var c = CARD[id];
    return '<li><a href="#card/' + id + '" data-peek="' + id + '"><span class="hwc-n">' + n + "</span>" + esc(title(c)) +
      (c.cost != null ? '<span class="hwc-cost">' + c.cost + "</span>" : "") + "</a></li>";
  }
  function starter(k) { var d = HW.decks[k]; return { herald: k, atlas: d.atlas, main: d.main }; }
  // A custom deck's Regions: its own choice, or its Herald's two.
  function regionsOf(d) { return d.regions || HW.decks[d.herald].atlas.filter(function (id) { return CARD[id].type === "Region"; }); }
  function custom(d) { return { herald: d.herald, atlas: regionsOf(d).concat(d.events), main: d.main }; }

  var saved = null, own = null, cloudState = "local";
  function live() { return (saved || []).filter(function (d) { return !d.deleted; }); }

  // A deck shown as its Herald's painting, like a deck box on Arena's deck screen.
  function deckTile(href, k, name, line, warn) {
    var h = CARD[D.heraldOf(k)], f = h.focus || [0.5, 0.4];
    var bg = "url(" + img("hero", h.id) + ")";
    return '<a class="hwc-deck" href="' + href + '"><span class="hwc-deck-art" style="background-image:' + bg + ";background-position:" + (f[0] * 100) + "% " + (f[1] * 100) + "%" +
      '"></span>' +
      '<span class="hwc-deck-cap"><b>' + esc(name) + "</b>" + line + (warn ? ' <em>' + esc(warn) + "</em>" : "") + "</span></a>";
  }

  function renderDecks() {
    var mine = live();
    $root.innerHTML = frame("decks",
      '<section class="hwc-band"><h2>Starter decks</h2><div class="hwc-decks">' + DECK_KEYS.map(function (k) {
        var d = HW.decks[k];
        return deckTile("#deck/" + k, k, heraldName(k), esc(d.realm) + " · " + esc(d.style));
      }).join("") + "</div></section>" +
      '<section class="hwc-band"><h2>Your decks</h2>' + accountLine() +
      '<div class="hwc-decks">' + mine.map(function (d) {
        var why = own ? D.problem(own, d) : null;
        return deckTile("#build/" + d.id, d.herald, d.name, esc(heraldName(d.herald)) + (why ? "" : " · Ready to play"), why);
      }).join("") +
      '<a class="hwc-deck hwc-new" href="#build/new"><span class="hwc-deck-cap"><b>New Deck</b>Start from a Herald, or copy a starter deck from its list.</span></a></div></section>');
  }
  function accountLine() {
    if (cloudState === "cloud") return '<p class="hwc-account">Saved to your Sanctum account, so your decks follow you to any device.</p>';
    var back = encodeURIComponent(location.pathname + location.hash);
    return '<p class="hwc-account">Decks are saved in this browser. ' +
      (ASSETS ? '<a href="/account/login?return_url=' + back + '">Sign in</a> to keep them on your Sanctum account.' : "Sign in on Sanctum to keep them on your account.") + "</p>";
  }

  function renderStarter(k) {
    var d = HW.decks[k];
    if (!d) { location.hash = "#decks"; return; }
    $root.innerHTML = frame("decks",
      '<a class="hwc-back" href="#decks">← Decks</a>' +
      '<div class="hwc-deckhead"><img src="' + img("card", D.heraldOf(k)) + '" alt=""><div><h1>' + esc(title(CARD[D.heraldOf(k)])) + "</h1>" +
      "<p>" + esc(d.realm) + " · " + esc(d.style) + ". 20 cards: the Herald, 2 Regions for the Atlas, and a 17-card deck with its 2 Events.</p>" +
      '<button class="hwc-plate hwc-primary" id="copy">Copy to Customise</button></div></div>' + decklist(starter(k)));
    on("copy", function () {
      var events = d.atlas.filter(function (id) { return CARD[id].type === "Event"; });
      var regions = d.atlas.filter(function (id) { return CARD[id].type === "Region"; });
      var copy = { id: D.newId(), name: heraldName(k) + " (custom)", herald: k, regions: regions, events: events, main: d.main.slice(), updated: Date.now() };
      persist((saved || []).concat([copy])).then(function () { location.hash = "#build/" + copy.id; });
    });
  }

  /* ---------- the builder ---------- */

  var draft = null, pool = { q: "", type: "" };

  function renderBuild(id) {
    if (!own) { $root.innerHTML = frame("decks", '<p class="hwc-empty">Loading your cards…</p>'); return; }
    if (!draft || draft.id !== id) {
      var found = live().filter(function (d) { return d.id === id; })[0];
      if (id !== "new" && !found) { location.hash = "#decks"; return; }
      draft = found ? JSON.parse(JSON.stringify(found)) : { id: "new", name: "", herald: own.decks[0], events: [], main: [] };
    }
    var why = D.problem(own, draft) || (draft.name.trim() ? null : "Name your deck.");
    var counts = {}; draft.main.forEach(function (x) { counts[x] = (counts[x] || 0) + 1; });
    var cards = HW.cards.filter(function (c) {
      if (c.type === "Herald" || !own.counts[c.id] || !E.inRealm(c, draft.herald)) return false;
      if (pool.type && c.type !== pool.type) return false;
      return !pool.q || searchable(c).indexOf(pool.q.toLowerCase()) > -1;
    }).sort(function (a, b) { return TYPES.indexOf(a.type) - TYPES.indexOf(b.type) || title(a).localeCompare(title(b)); });

    $root.innerHTML = frame("decks",
      '<a class="hwc-back" href="#decks">← Decks</a>' +
      '<div class="hwc-builder"><div class="hwc-pool">' +
      '<div class="hwc-search"><input id="pq" type="search" placeholder="Search your cards" value="' + esc(pool.q) + '" aria-label="Search your cards"></div>' +
      '<div class="hwc-chips">' + ["", "Unit", "Evolving Unit", "Equipment", "Path", "Action", "Spell"].map(function (t) {
        return '<button class="hwc-chip' + (pool.type === t ? " hwc-on" : "") + '" data-ptype="' + t + '">' + (t || "Every Type") + "</button>"; }).join("") + "</div>" +
      '<p class="hwc-count">Tap a card to add it. Your Herald\'s realm plus neutral cards: up to 2 of each neutral card, 1 of each realm card and one Path, from what you own.</p>' +
      '<div class="hwc-grid hwc-grid-sm">' + cards.map(function (c) {
        var max = D.limit(own, c.id), n = counts[c.id] || 0, full = n >= max || draft.main.length >= E.MAIN;
        return '<button class="hwc-tile' + (full ? " hwc-full" : "") + '" data-add="' + c.id + '" title="' + esc(title(c)) + '"><img loading="lazy" src="' + cardImg(c.id) + '" alt="' + esc(title(c)) + '">' +
          '<span class="hwc-have">' + n + "/" + max + "</span></button>";
      }).join("") + "</div></div>" +

      '<aside class="hwc-draft"><input id="dname" class="hwc-name" maxlength="40" placeholder="Name your deck" value="' + esc(draft.name) + '" aria-label="Deck name">' +
      "<h3>Herald</h3><div class=\"hwc-heralds\">" + own.decks.map(function (k) {
        return '<button class="' + (draft.herald === k ? "hwc-on" : "") + '" data-herald="' + k + '" title="' + esc(heraldName(k)) + '"><img src="' + img("card", D.heraldOf(k)) + '" alt="' + esc(heraldName(k)) + '"></button>'; }).join("") + "</div>" +
      "<h3>Regions <span>" + regionsOf(draft).length + "/2</span></h3><div class=\"hwc-events\">" + HW.atlas.filter(function (x) { return x.type === "Region"; }).map(function (x) {
        return '<button class="hwc-chip' + (regionsOf(draft).indexOf(x.id) > -1 ? " hwc-on" : "") + '" data-region="' + x.id + '" data-peek="' + x.id + '">' + esc(x.location) + ' <i>' + esc(x.realm) + "</i></button>"; }).join("") + "</div>" +
      '<p class="hwc-small">Regions from ' + esc(HW.decks[draft.herald].realm) + " bring your Homecoming; any other realm is yours to choose for its rules alone.</p>" +
      "<h3>Events <span>" + draft.events.length + "/2</span></h3><div class=\"hwc-events\">" + own.events.map(function (x) {
        return '<button class="hwc-chip' + (draft.events.indexOf(x) > -1 ? " hwc-on" : "") + '" data-event="' + x + '" data-peek="' + x + '">' + esc(CARD[x].name) + "</button>"; }).join("") + "</div>" +
      "<h3>Main deck <span>" + draft.main.length + "/" + E.MAIN + "</span></h3>" +
      '<ul class="hwc-draftlist">' + Object.keys(counts).sort(function (a, b) { return TYPES.indexOf(CARD[a].type) - TYPES.indexOf(CARD[b].type) || title(CARD[a]).localeCompare(title(CARD[b])); }).map(function (x) {
        return '<li><button data-remove="' + x + '" data-peek="' + x + '" title="Remove one"><span class="hwc-n">' + counts[x] + "</span>" + esc(title(CARD[x])) + '<span class="hwc-x">−</span></button></li>'; }).join("") +
      (draft.main.length ? "" : '<li class="hwc-small">No cards yet.</li>') + "</ul>" +
      '<p class="hwc-why' + (why ? "" : " hwc-ok") + '">' + esc(why || "Ready to play.") + "</p>" +
      '<div class="hwc-row"><button class="hwc-plate hwc-primary" id="save">Save Deck</button>' +
      (draft.id !== "new" ? '<button class="hwc-plate hwc-quiet" id="del">Delete</button>' : "") + "</div>" + accountLine() + "</aside></div>");

    var pq = document.getElementById("pq");
    pq.oninput = function () { pool.q = pq.value; var at = pq.selectionStart; renderBuild(id); var n = document.getElementById("pq"); n.focus(); n.setSelectionRange(at, at); };
    document.getElementById("dname").oninput = function (e) { draft.name = e.target.value; };
    document.getElementById("dname").onchange = function () { renderBuild(id); };
    each("[data-ptype]", function (b) { b.onclick = function () { pool.type = b.getAttribute("data-ptype"); renderBuild(id); }; });
    // a new Herald takes only its own realm's cards: the rest leave the deck
    each("[data-herald]", function (b) { b.onclick = function () { draft.herald = b.getAttribute("data-herald"); draft.main = draft.main.filter(function (x) { return E.inRealm(CARD[x], draft.herald); }); renderBuild(id); }; });
    each("[data-region]", function (b) {
      b.onclick = function () {
        var x = b.getAttribute("data-region"), r = regionsOf(draft).slice(), i = r.indexOf(x);
        if (i > -1) r.splice(i, 1); else { if (r.length >= 2) r.shift(); r.push(x); }
        draft.regions = r; renderBuild(id);
      };
    });
    each("[data-event]", function (b) {
      b.onclick = function () {
        var x = b.getAttribute("data-event"), i = draft.events.indexOf(x);
        if (i > -1) draft.events.splice(i, 1); else { if (draft.events.length >= 2) draft.events.shift(); draft.events.push(x); }
        renderBuild(id);
      };
    });
    each("[data-add]", function (b) {
      b.onclick = function () {
        var x = b.getAttribute("data-add");
        if (draft.main.length >= E.MAIN) { note("The main deck is full. Remove a card first."); return; }
        if ((counts[x] || 0) >= D.limit(own, x)) { note("You can't add another " + CARD[x].name + "."); return; }
        draft.main.push(x); renderBuild(id);
      };
    });
    each("[data-remove]", function (b) { b.onclick = function () { draft.main.splice(draft.main.indexOf(b.getAttribute("data-remove")), 1); renderBuild(id); }; });
    on("save", function () {
      draft.name = draft.name.trim();
      if (!draft.name) { note("Name your deck first."); return; }
      var d = JSON.parse(JSON.stringify(draft)); d.updated = Date.now();
      if (d.id === "new") d.id = D.newId();
      var list = (saved || []).filter(function (x) { return x.id !== d.id; }).concat([d]);
      persist(list).then(function () { draft = d; note(D.problem(own, d) ? "Saved. Finish it to play it." : "Saved. It's ready to play."); if (location.hash !== "#build/" + d.id) location.hash = "#build/" + d.id; else renderBuild(d.id); });
    });
    on("del", function () {
      if (!confirm("Delete " + (draft.name || "this deck") + "?")) return;
      var list = (saved || []).map(function (x) { return x.id === draft.id ? { id: x.id, deleted: true, updated: Date.now() } : x; });
      draft = null;
      persist(list).then(function () { location.hash = "#decks"; });
    });
  }

  function persist(list) {
    saved = list;
    return D.save(list).then(function (l) { saved = l; }, function (err) { note((err && err.message) || "Your deck couldn't be saved to your account. It's kept in this browser."); });
  }

  /* ---------- rules ---------- */

  function renderRules() {
    var R = HW.rules;
    $root.innerHTML = frame("rules",
      '<p class="hwc-intro">' + R.intro + "</p>" +
      '<div class="hwc-rules">' + R.sections.map(function (s) {
        return "<section><h2>" + esc(s.title) + "</h2><ul>" + s.points.map(function (p) { return "<li>" + p + "</li>"; }).join("") + "</ul></section>"; }).join("") + "</div>" +
      '<h2 class="hwc-h">Keywords</h2><dl class="hwc-words">' + R.keywords.map(function (k) { return "<dt>" + esc(k.term) + "</dt><dd>" + k.text + "</dd>"; }).join("") + "</dl>" +
      '<h2 class="hwc-h">Symbols</h2><div class="hwc-symbols"><ul>' + R.orbs.map(function (o) {
        return '<li><i class="hwc-orbimg" style="background-image:url(' + img("ui", o.img) + ')"></i><div><b>' + esc(o.name) + "</b><br>" + o.text + "</div></li>"; }).join("") + "</ul><ul>" +
      R.types.map(function (t) { return '<li><i class="hwc-orbimg" style="background-image:url(' + img("ui", "type-" + t.icon) + ')"></i><div><b>' + t.type + "</b><br>" + t.text + "</div></li>"; }).join("") + "</ul></div>" +
      '<h2 class="hwc-h">Building a deck</h2><ul class="hwc-plain">' + R.building.map(function (b) { return "<li>" + b + "</li>"; }).join("") + "</ul>");
  }

  /* ---------- plumbing ---------- */

  function each(sel, fn) { Array.prototype.forEach.call($root.querySelectorAll(sel), fn); }
  function on(id, fn) { var el = document.getElementById(id); if (el) el.onclick = fn; }
  var noteTimer = 0;
  function note(text) {
    var n = document.getElementById("hwc-note");
    if (!n) { n = document.createElement("div"); n.id = "hwc-note"; n.className = "hwc-toast"; n.setAttribute("role", "status"); document.body.appendChild(n); }
    n.textContent = text; n.hidden = false;
    clearTimeout(noteTimer); noteTimer = setTimeout(function () { n.hidden = true; }, 3000);
  }

  // Hovering a decklist line shows the card beside the pointer.
  var peek = document.createElement("img");
  peek.className = "hwc-peek"; peek.hidden = true; peek.alt = "";
  document.body.appendChild(peek);
  document.addEventListener("mouseover", function (e) {
    var a = e.target.closest && e.target.closest("[data-peek]");
    if (!a || matchMedia("(pointer: coarse)").matches) { peek.hidden = true; return; }
    var id = a.getAttribute("data-peek");
    peek.src = cardImg(id); peek.className = "hwc-peek" + (wide(id) ? " hwc-wide" : ""); peek.hidden = false;
  });
  document.addEventListener("mousemove", function (e) {
    if (peek.hidden) return;
    var w = peek.offsetWidth || 250, h = peek.offsetHeight || 350;
    peek.style.left = Math.min(innerWidth - w - 10, e.clientX + 24) + "px";
    peek.style.top = Math.max(10, Math.min(innerHeight - h - 10, e.clientY - h / 2)) + "px";
  });

  function route(keepScroll) {
    peek.hidden = true;
    var h = location.hash.replace(/^#/, "").split("/"), view = h[0] || "cards";
    if (keepScroll !== true) menuOpen = false;
    if (view !== "build") draft = null;
    if (view === "card") renderCard(h[1]);
    else if (view === "decks") renderDecks();
    else if (view === "deck") renderStarter(h[1]);
    else if (view === "build") renderBuild(h[1]);
    else if (view === "rules") renderRules();
    else renderCards();
    if (view !== "cards" && keepScroll !== true) { scrollTo(0, 0); $root.scrollTop = 0; }
  }
  addEventListener("hashchange", function () { route(); });
  route();

  // Sign in through Sanctum if we can, then load what this player owns and has saved.
  D.connect().then(function (signedIn) {
    cloudState = signedIn ? "cloud" : "local";
    return Promise.all([D.owned(), D.load()]);
  }).then(function (r) { own = r[0]; saved = r[1]; route(); }, function () {
    own = own || { decks: DECK_KEYS, counts: {}, events: [] }; saved = saved || []; route();
  });
})();
